import { useState } from 'react';
import { Title } from 'react-admin';
import { useQuery } from '@apollo/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentsIcon from '@mui/icons-material/Payments';
import WarningIcon from '@mui/icons-material/Warning';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

import { GET_PAYMENT_STATS, GET_FAILED_WEBHOOKS, GET_PAYMENT_METHOD_DISTRIBUTION } from './payment.queries';
import {
  PaymentStats,
  FailedWebhook,
  PaymentMethodDistribution,
  formatAmount,
  formatPercentage,
  PROVIDER_LABELS,
  PROVIDER_COLORS,
  METHOD_LABELS,
} from './payment.types';

/**
 * KPI Card Component
 */
interface KpiCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  trend?: number;
  loading?: boolean;
}

function KpiCard({ title, value, subtitle, icon, color, trend, loading }: KpiCardProps) {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          backgroundColor: color,
        },
      }}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
            {title}
          </Typography>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: `${color}15`,
              color: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>

        {loading ? (
          <>
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton variant="text" width="40%" />
          </>
        ) : (
          <>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
              {value}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {trend !== undefined && (
                <Chip
                  size="small"
                  icon={trend >= 0 ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />}
                  label={`${trend >= 0 ? '+' : ''}${trend}%`}
                  color={trend >= 0 ? 'success' : 'error'}
                  variant="outlined"
                  sx={{ height: 24 }}
                />
              )}
              {subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Date range options
 */
type DateRange = '7d' | '30d' | '90d' | 'year';

function getDateRange(range: DateRange): { startDate: Date; endDate: Date } {
  const endDate = new Date();
  const startDate = new Date();

  switch (range) {
    case '7d':
      startDate.setDate(endDate.getDate() - 7);
      break;
    case '30d':
      startDate.setDate(endDate.getDate() - 30);
      break;
    case '90d':
      startDate.setDate(endDate.getDate() - 90);
      break;
    case 'year':
      startDate.setFullYear(endDate.getFullYear() - 1);
      break;
  }

  return { startDate, endDate };
}

/**
 * Payment Dashboard Component
 */
export function PaymentDashboard() {
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const { startDate, endDate } = getDateRange(dateRange);

  // Fetch payment stats
  const {
    data: statsData,
    loading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useQuery<{ paymentStats: PaymentStats }>(GET_PAYMENT_STATS, {
    variables: {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    },
  });

  // Fetch failed webhooks
  const { data: webhooksData } = useQuery<{ failedWebhooks: FailedWebhook[] }>(
    GET_FAILED_WEBHOOKS,
    { variables: { limit: 5 } }
  );

  // Fetch payment method distribution
  const { data: distributionData, loading: distributionLoading } = useQuery<{
    paymentMethodDistribution: PaymentMethodDistribution[];
  }>(GET_PAYMENT_METHOD_DISTRIBUTION, {
    variables: {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    },
  });

  const stats = statsData?.paymentStats;
  const failedWebhooks = webhooksData?.failedWebhooks || [];
  const methodDistribution = distributionData?.paymentMethodDistribution || [];

  // Chart colors
  const CHART_COLORS = ['#10B981', '#6366F1', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  return (
    <Box>
      <Title title="Tableau de bord Paiements" />

      {/* Header with date filter */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Vue d'ensemble des paiements
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Période</InputLabel>
            <Select
              value={dateRange}
              label="Période"
              onChange={(e) => setDateRange(e.target.value as DateRange)}
            >
              <MenuItem value="7d">7 derniers jours</MenuItem>
              <MenuItem value="30d">30 derniers jours</MenuItem>
              <MenuItem value="90d">90 derniers jours</MenuItem>
              <MenuItem value="year">Cette année</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Actualiser">
            <IconButton onClick={() => refetchStats()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error alert */}
      {statsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Erreur</AlertTitle>
          Impossible de charger les statistiques de paiement.
        </Alert>
      )}

      {/* Failed webhooks alert */}
      {failedWebhooks.length > 0 && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{ mb: 3 }}
          action={
            <Chip
              label={`${failedWebhooks.length} webhooks échoués`}
              color="warning"
              size="small"
            />
          }
        >
          <AlertTitle>Attention</AlertTitle>
          Des webhooks ont échoué et nécessitent votre attention. Vérifiez la liste des transactions pour plus de détails.
        </Alert>
      )}

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Volume total"
            value={stats ? formatAmount(stats.totalVolume) : '-'}
            subtitle="Tous providers"
            icon={<AccountBalanceWalletIcon />}
            color="#10B981"
            trend={12}
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Transactions"
            value={stats ? stats.totalTransactions.toLocaleString('fr-FR') : '-'}
            subtitle="Total"
            icon={<ReceiptLongIcon />}
            color="#6366F1"
            trend={8}
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Taux de succès"
            value={stats ? formatPercentage(stats.successRate) : '-'}
            subtitle="Moyenne"
            icon={<CheckCircleIcon />}
            color="#F59E0B"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Montant moyen"
            value={stats ? formatAmount(stats.averageAmount) : '-'}
            subtitle="Par transaction"
            icon={<PaymentsIcon />}
            color="#EF4444"
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* Provider KPIs */}
      {stats?.volumeByProvider && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {stats.volumeByProvider.map((provider) => (
            <Grid item xs={12} sm={6} md={4} key={provider.provider}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {PROVIDER_LABELS[provider.provider]}
                    </Typography>
                    <Chip
                      label={`${formatPercentage(provider.successRate)} succès`}
                      size="small"
                      sx={{
                        backgroundColor: `${PROVIDER_COLORS[provider.provider]}15`,
                        color: PROVIDER_COLORS[provider.provider],
                      }}
                    />
                  </Box>
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                    {formatAmount(provider.volume)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {provider.count.toLocaleString('fr-FR')} transactions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Volume trend chart */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Évolution du volume
              </Typography>
              {statsLoading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : stats?.recentTrend ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={stats.recentTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) =>
                        new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
                      }
                    />
                    <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`} />
                    <RechartsTooltip
                      formatter={(value: number) => [formatAmount(value), 'Volume']}
                      labelFormatter={(label) =>
                        new Date(label).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                        })
                      }
                    />
                    <Area
                      type="monotone"
                      dataKey="volume"
                      stroke="#10B981"
                      fill="#10B98120"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    height: 300,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#F3F4F6',
                    borderRadius: 2,
                  }}
                >
                  <Typography color="text.secondary">Aucune donnée disponible</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Payment method distribution */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Méthodes de paiement
              </Typography>
              {distributionLoading ? (
                <Skeleton variant="circular" width={250} height={250} sx={{ mx: 'auto', mt: 2 }} />
              ) : methodDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={methodDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="percentage"
                      nameKey="method"
                      label={({ method, percentage }) =>
                        `${METHOD_LABELS[method as keyof typeof METHOD_LABELS] || method}: ${percentage.toFixed(0)}%`
                      }
                      labelLine={false}
                    >
                      {methodDistribution.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend
                      formatter={(value) => METHOD_LABELS[value as keyof typeof METHOD_LABELS] || value}
                    />
                    <RechartsTooltip
                      formatter={(value: number, name: string) => [
                        `${value.toFixed(1)}%`,
                        METHOD_LABELS[name as keyof typeof METHOD_LABELS] || name,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    height: 300,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#F3F4F6',
                    borderRadius: 2,
                  }}
                >
                  <Typography color="text.secondary">Aucune donnée disponible</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default PaymentDashboard;
